import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Form, Spinner } from "react-bootstrap";
import {
  FaWhatsapp,
  FaMapMarkerAlt,
  FaPhone,
  FaMoneyBillWave,
  FaCreditCard,
  FaArrowLeft,
} from "react-icons/fa";
import styles from "./style.module.css";
import { getStoreData, orderRequest } from "../../api";
import CustomAlert from "../../components/ui/CustomAlert";

const formatMsisdn = (msisdn) => {
  if (!msisdn) return "";
  return msisdn
    .toString()
    .replace(/(\d{2})(\d{3})(\d{2})(\d{2})/, "$1 $2 $3 $4");
};

const formatPhone = (number) => {
  if (!number) return "";
  return number
    .toString()
    .replace(/(\+?\d{3})(\d{2})(\d{3})(\d{2})(\d{2})/, "$1 $2 $3 $4 $5");
};

const formatGiftText = (minutes, dataMb) => {
  const parts = [];
  if (dataMb) {
    if (dataMb >= 1000) {
      const gbValue = (dataMb / 1000).toFixed(1).replace(/\.0$/, "");
      parts.push(`${gbValue} GB`);
    } else {
      parts.push(`${dataMb} MB`);
    }
  }
  if (minutes) parts.push(`${minutes} dəq.`);
  return parts.length > 0 ? parts.join(", ") : "Hədiyyəsiz";
};

const formatLocalContact = (value) => {
  let digits = (value || "").toString().replace(/\D/g, "");
  if (!digits.length) return "(0";
  if (digits[0] !== "0") digits = "0" + digits;
  digits = digits.slice(0, 10);

  if (digits.length === 1) return "(0";
  if (digits.length <= 3) return `(${digits}`;

  let result = `(${digits.slice(0, 3)}) `;
  if (digits.length <= 6) return result + digits.slice(3);

  result += `${digits.slice(3, 6)}-${digits.slice(
    6,
    Math.min(8, digits.length)
  )}`;
  if (digits.length <= 8) return result;
  return result + `-${digits.slice(8, 10)}`;
};

const OrderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getNumberListing = () => {
    const stateListing = location.state?.numberListing;
    if (stateListing) return stateListing;

    try {
      const persisted = sessionStorage.getItem("order:numberListing");
      return persisted ? JSON.parse(persisted) : null;
    } catch {
      return null;
    }
  };

  const numberListing = getNumberListing();
  const msisdn = numberListing?.msisdn || "";
  const storeID = numberListing?.storeID || "";

  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [alert, setAlert] = useState(null);
  const [formData, setFormData] = useState({ name: "", contactNumber: "(0" });
  const [formErrors, setFormErrors] = useState({});
  const [cashSubmitting, setCashSubmitting] = useState(false);
  const [onlineSubmitting, setOnlineSubmitting] = useState(false);

  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = "Ad və soyad daxil edilməlidir";
    }

    const digits = formData.contactNumber.replace(/\D/g, "");
    if (!formData.contactNumber.trim()) {
      errors.contactNumber = "Əlaqə nömrəsi daxil edilməlidir";
    } else if (!(digits.length === 10 && digits[0] === "0")) {
      errors.contactNumber = "Düzgün əlaqə nömrəsi daxil edin";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (numberListing) {
      try {
        sessionStorage.setItem(
          "order:numberListing",
          JSON.stringify(numberListing)
        );
      } catch {}
    }

    if (!msisdn || !storeID) {
      setError("Sifariş üçün məlumatlar tapılmadı.");
      setLoading(false);
      return;
    }

    let mounted = true;
    getStoreData(storeID)
      .then((data) => {
        if (mounted) setStore(data);
      })
      .catch((e) => {
        console.error(e);
        if (mounted) setError("Mağaza məlumatları tapılmadı.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [msisdn, storeID, numberListing]);

  const handleChange = (field, value) => {
    if (field === "name" && !/^[a-zA-Z\s]*$/.test(value)) return;

    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleCashOrder = async () => {
    if (!validate()) return;

    setCashSubmitting(true);
    try {
      const name = formData.name.trim();

      const orderResponse = await orderRequest({
        msisdn,
        contactNumber: formData.contactNumber,
        name,
      });

      const params = new URLSearchParams({
        name,
        number: formData.contactNumber,
        msisdn,
        storeId: storeID,
        orderId: orderResponse?.orderId || "",
      });
      navigate(`/order-result?${params}`);
    } catch (e) {
      console.error(e);
      setAlert({
        type: "error",
        message: "Xəta baş verdi. Yenidən cəhd edin.",
      });
    } finally {
      setCashSubmitting(false);
    }
  };

  const handleOnlinePay = async () => {
    if (!validate()) return;

    setOnlineSubmitting(true);
    try {
      const name = formData.name.trim();
      const params = new URLSearchParams({
        name,
        number: formData.contactNumber,
        msisdn,
        storeId: storeID,
      });

      const orderResponse = await orderRequest({
        msisdn,
        contactNumber: formData.contactNumber,
        name,
        hppRedirectUrl: `${window.location.origin}/order-result?${params}`,
      });

      if (orderResponse?.paymentUrl) {
        window.location.assign(orderResponse.paymentUrl);
      } else {
        throw new Error("Yönləndirmə URL-i tapılmadı");
      }
    } catch (e) {
      console.error(e);
      setAlert({ type: "error", message: "Ödəniş sifarişi yaradılmadı." });
    } finally {
      setOnlineSubmitting(false);
    }
  };

  const formattedNumber = formatMsisdn(msisdn);
  const typeLabel =
    numberListing?.paymentType === "postpaid" ? "Fakturalı" : "Fakturasız";
  const formattedPrice =
    numberListing?.price != null ? `${numberListing.price} ₼` : "";
  const giftText = formatGiftText(
    numberListing?.includedMinutes,
    numberListing?.includedDataMb
  );

  if (loading) {
    return (
      <div className={styles.centerWrap}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.centerWrap}>
        <p className="mb-3">{error}</p>
        <Button variant="secondary" onClick={() => navigate("/")}>
          Ana səhifə
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.pageWrap}>
      <div className={styles.backRow}>
        <Button
          variant="link"
          className={styles.backButton}
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft className={styles.backIcon} /> Geriyə
        </Button>
      </div>

      {alert && (
        <CustomAlert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert(null)}
        />
      )}

      <div className={styles.topRow}>
        <div className={styles.storeDetails}>
          <h1 className={styles.title}>{store?.storeName || "Mağaza"}</h1>
          <div className={styles.storeLinks}>
            {store?.contactNumber && (
              <a
                className={styles.infoItem}
                href={`tel:${store.contactNumber}`}
              >
                <FaPhone className={styles.icon} />
                <span>{formatPhone(store.contactNumber)}</span>
              </a>
            )}
            {store?.whatsappNumber && (
              <a
                className={styles.infoItem}
                href={`https://wa.me/${store.whatsappNumber}?text=Salam, ${msisdn} nömrəsi barədə məlumat almaq istəyirəm - SimSim müştərisi.`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaWhatsapp className={styles.icon} />
                <span>{formatPhone(store.whatsappNumber)}</span>
              </a>
            )}
            {store?.lat && store?.lng && (
              <a
                className={styles.infoItem}
                href={`https://www.google.com/maps?q=${store.lat},${store.lng}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaMapMarkerAlt className={styles.icon} />
                <span>Xəritədə aç</span>
              </a>
            )}
          </div>
        </div>

        <div className={styles.numberSide}>
          <div className={styles.numberValue}>{formattedNumber}</div>
          <div className={styles.numberMeta}>
            {[typeLabel, formattedPrice, giftText].filter(Boolean).join(" • ")}
          </div>
        </div>
      </div>

      <div className={styles.formWrap}>
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Müştəri məlumatları</h2>

          <Form onSubmit={(e) => e.preventDefault()}>
            <div className="mb-3">
              <Form.Label className={styles.formLabel}>Ad və Soyad</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Ad və soyadınızı daxil edin"
                className={`${styles.formInput} ${
                  formErrors.name ? styles.errorInput : ""
                }`}
              />
              {formErrors.name && (
                <div className={styles.errorText}>{formErrors.name}</div>
              )}
            </div>

            <div className="mb-0">
              <Form.Label className={styles.formLabel}>
                Əlaqə Nömrəsi
              </Form.Label>
              <Form.Control
                type="tel"
                inputMode="numeric"
                pattern="\\(0\\d{2}\\)\\s\\d{3}-\\d{2}-\\d{2}"
                value={formData.contactNumber}
                onChange={(e) => {
                  const formatted = formatLocalContact(e.target.value);
                  handleChange("contactNumber", formatted);
                }}
                onKeyDown={(e) => {
                  const target = e.target;
                  const start = target.selectionStart ?? 0;
                  const end = target.selectionEnd ?? start;
                  const isDeletingPrefix =
                    start <= 2 &&
                    end <= 2 &&
                    (e.key === "Backspace" || e.key === "Delete");
                  if (isDeletingPrefix) e.preventDefault();
                }}
                placeholder="(050) 247-37-36"
                className={`${styles.formInput} ${
                  formErrors.contactNumber ? styles.errorInput : ""
                }`}
              />
              {formErrors.contactNumber && (
                <div className={styles.errorText}>
                  {formErrors.contactNumber}
                </div>
              )}

              <div className={styles.formActions}>
                <Button
                  variant="dark"
                  onClick={handleCashOrder}
                  disabled={cashSubmitting || onlineSubmitting}
                  className={styles.actionButton}
                >
                  <FaMoneyBillWave className={styles.btnIcon} />
                  {cashSubmitting ? "Yüklənir..." : "Nağd Ödə"}
                </Button>
                {storeID === "simsim" && (
                  <Button
                    variant="success"
                    onClick={handleOnlinePay}
                    disabled={cashSubmitting || onlineSubmitting}
                    className={styles.actionButton}
                  >
                    <FaCreditCard className={styles.btnIcon} />
                    {onlineSubmitting ? "Yüklənir..." : "Onlayn Ödə"}
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </section>
      </div>
    </div>
  );
};

export default OrderPage;
